/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 22/12/21
 */

import { useAuth } from '@/components/auth/useAuth';
import config from '@/config';
import { createUnprovisionedContext } from '@/wpt-lib/utils/contextHelpers';
import axios, { AxiosInstance } from 'axios';
import React, { useContext, useRef } from 'react';

interface IAxiosContext {
    axiosInstance: AxiosInstance;
}

export const AxiosContext = createUnprovisionedContext<IAxiosContext>('AxiosContext');

const createAxiosInstance = () => {
    return axios.create({
        baseURL: config.api.baseUrl,
        timeout: 30000,
    });
};

export const AxiosProvider = ({ children }: { children: JSX.Element }) => {
    const axiosInstanceRef = useRef<AxiosInstance>(createAxiosInstance());

    const { authState } = useAuth();
    axiosInstanceRef.current.defaults.headers.common.Authorization = `Bearer ${authState.accessToken}`;

    const context: IAxiosContext = {
        axiosInstance: axiosInstanceRef.current,
    };

    return <AxiosContext.Provider value={context}>{children}</AxiosContext.Provider>;
};

export const useAxios = () => {
    return useContext(AxiosContext);
};

export default AxiosProvider;
